// components/CustomStatistic.tsx
import React from 'react';
import {
    Typography,
    Space,
    Tooltip,
    Icon
} from '@douyinfe/semi-ui';
import {
    IconTextRectangle,
    IconArrowUp,
    IconArrowDown,
    IconRingChartStroked,
    IconFlag,
    IconActivity,
    IconFilledArrowUp,
    IconFilledArrowDown
} from '@douyinfe/semi-icons';

const { Text } = Typography;

export interface StatisticProps {
    /** 统计数值 */
    value?: number | string;
    /** 标题 */
    title?: React.ReactNode;
    /** 前缀图标或文字 */
    prefix?: React.ReactNode;
    /** 后缀图标或文字 */
    suffix?: React.ReactNode;
    /** 格式化显示值 */
    formatter?: (value: number | string) => string;
    /** 数值精度 */
    precision?: number;
    /** 千分位分隔符 */
    groupSeparator?: boolean;
    /** 数值样式 */
    valueStyle?: React.CSSProperties;
    /** 标题样式 */
    titleStyle?: React.CSSProperties;
    /** 布局方式 */
    layout?: 'horizontal' | 'vertical';
    /** 卡片模式 */
    card?: boolean;
    /** 趋势：up/down */
    trend?: 'up' | 'down' | 'neutral';
    /** 趋势百分比 */
    trendPercent?: number;
    /** 加载状态 */
    loading?: boolean;
    /** 是否显示趋势图标 */
    showTrend?: boolean;
    /** 统计类型 */
    type?: 'default' | 'success' | 'warning' | 'danger' | 'info';
    /** 点击事件 */
    onClick?: () => void;
    /** 自定义样式类名 */
    className?: string;
    /** 内联样式 */
    style?: React.CSSProperties;
}

const Statistic: React.FC<StatisticProps> = ({
    value = 0,
    title,
    prefix,
    suffix,
    formatter,
    precision = 2,
    groupSeparator = true,
    valueStyle = {},
    titleStyle = {},
    layout = 'vertical',
    card = false,
    trend,
    trendPercent,
    loading = false,
    showTrend = false,
    type = 'default',
    onClick,
    className = '',
    style = {}
}) => {
    // 格式化数值
    const formatValue = (val: number | string): string => {
        if (formatter) {
            return formatter(val);
        }
        
        let num = typeof val === 'string' ? parseFloat(val) : val;
        
        if (isNaN(num)) {
            return '--';
        }
        
        // 处理精度
        let formatted = num.toFixed(precision);
        
        // 千分位分隔
        if (groupSeparator) {
            const parts = formatted.split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            formatted = parts.join('.');
        }
        
        return formatted;
    };
    
    // 获取趋势图标
    const getTrendIcon = () => {
        if (!trend) return null;
    
        switch (trend) {
            case 'up':
                return <IconFilledArrowUp size='small' style={{ color: 'var(--semi-color-success)' }} />;
            case 'down':
                return <IconArrowDown size='small' style={{ color: 'var(--semi-color-danger)' }} />;
            default:
                return null;
        }
    };
    
    // 获取类型对应的颜色
    const getTypeColor = (): string => {
        switch (type) {
            case 'success':
                return 'var(--semi-color-success)';
            case 'warning':
                return 'var(--semi-color-warning)';
            case 'danger':
                return 'var(--semi-color-danger)';
            case 'info':
                return 'var(--semi-color-info)';
            default:
                return 'var(--semi-color-text-0)';
        }
    };
    
    // 渲染加载状态
    if (loading) {
        return (
            <div 
                className={`semi-statistic ${className}`}
                style={{ 
                    ...style,
                    opacity: 0.6 
                }}
            >
                <Space vertical={layout === 'vertical'} spacing={4}>
                    {title && (
                        <Text 
                            type="tertiary" 
                            style={titleStyle}
                        >
                            {title}
                        </Text>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{
                            width: 80,
                            height: 24,
                            background: 'var(--semi-color-fill-1)',
                            borderRadius: 4,
                            animation: 'pulse 1.5s infinite'
                        }} />
                    </div>
                </Space>
            </div>
        );
    }
    
    // 渲染趋势百分比
    const renderTrendPercent = () => {
        if (!trendPercent) return null;
        
        const isPositive = trendPercent > 0;
        const color = isPositive ? 'var(--semi-color-success)' : 'var(--semi-color-danger)';
        
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                marginLeft: 8,
                fontSize: 12
            }}>
              
                <Text 
                    size="small"
                    style={{ color }}
                >
                    {Math.abs(trendPercent).toFixed(1)}%
                </Text>
            </div>
        );
    };
    
    const content = (
        <div 
            className={`semi-statistic ${className} ${card ? 'semi-statistic-card' : ''}`}
            style={{
                ...style,
                padding: card ? '16px' : 0,
                background: card ? 'var(--semi-color-fill-0)' : 'transparent',
                borderRadius: card ? '8px' : 0,
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.2s ease'
            }}
            onClick={onClick}
            onMouseEnter={onClick ? (e) => {
                e.currentTarget.style.background = 'var(--semi-color-fill-1)';
            } : undefined}
            onMouseLeave={onClick ? (e) => {
                e.currentTarget.style.background = card ? 'var(--semi-color-fill-0)' : 'transparent';
            } : undefined}
        >
            <Space 
                vertical={layout === 'vertical'} 
                spacing={layout === 'vertical' ? 8 : 12}
                align={layout === 'horizontal' ? 'center' : 'start'}
                style={{ width: '100%' }}
            >
                {/* 标题 */}
                {title && (
                    <Text 
                        type="tertiary" 
                        style={{
                            fontSize: 14,
                            ...titleStyle
                        }}
                    >
                        {title}
                    </Text>
                )}
                
                {/* 数值区域 */}
                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                    {/* 前缀 */}
                    {prefix && (
                        <div style={{ marginRight: 8, display: 'flex', alignItems: 'center' }}>
                            {typeof prefix === 'string' ? (
                                <Text type="tertiary">{prefix}</Text>
                            ) : prefix}
                        </div>
                    )}
                    
                    {/* 数值 */}
                    <Text 
                        strong
                        style={{
                            fontSize: 24,
                            color: getTypeColor(),
                            lineHeight: 1,
                            ...valueStyle
                        }}
                    >
                        {formatValue(value)}
                    </Text>
                    
                    {/* 趋势图标 */}
                    {showTrend && getTrendIcon()}
                    
                    {/* 趋势百分比 */}
                    {trendPercent && renderTrendPercent()}
                    
                    {/* 后缀 */}
                    {suffix && (
                        <div style={{ marginLeft: 8, display: 'flex', alignItems: 'center' }}>
                            {typeof suffix === 'string' ? (
                                <Text style={{ fontSize: 14 }}>{suffix}</Text>
                            ) : suffix}
                        </div>
                    )}
                </div>
            </Space>
        </div>
    );
    
    return content;
};

// 创建预设的统计组件
export const StatisticCard: React.FC<StatisticProps> = (props) => (
    <Statistic {...props} card />
);

export const CountUpStatistic: React.FC<StatisticProps & { duration?: number }> = ({ 
    duration = 1500, 
    ...props 
}) => {
    const [displayValue, setDisplayValue] = React.useState(0);
    const targetValue = typeof props.value === 'string' ? parseFloat(props.value) : (props.value || 0);
    
    React.useEffect(() => {
        if (isNaN(targetValue)) return;
        
        let startTime: number;
        let animationFrameId: number;
        
        const animate = (currentTime: number) => {
            if (!startTime) startTime = currentTime;
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // 使用缓动函数
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const currentValue = Math.floor(targetValue * easeOutQuart);
            
            setDisplayValue(currentValue);
            
            if (progress < 1) {
                animationFrameId = requestAnimationFrame(animate);
            } else {
                setDisplayValue(targetValue);
            }
        };
        
        animationFrameId = requestAnimationFrame(animate);
        
        return () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
        };
    }, [targetValue, duration]);
    
    return <Statistic {...props} value={displayValue} />;
};

export default Statistic;